<template>
  <div class="flex-1 flex flex-col p-2 bg-white rounded-sm mt-2">
    <div class="flex items-center justify-between mb-3 pl-1">
      <h3 class="text-lg font-bold text-gray-800 flex items-center">
        
        Non-Individual Applications
      </h3>
    </div>
    <div class="grid grid-cols-6 gap-2 py-2" v-if="stats">
      <div class="stat-card text-gray-500">
        <label class="text-2xl font-bold">{{ stats.pending }}</label>
        <label class="text-sm">Pending</label>
      </div>
      <div class="stat-card">
        <label class="text-2xl font-bold">{{ stats.submitted }}</label>
        <label class="text-sm">Submitted</label>
      </div>
      <div class="stat-card text-blue">
        <label class="text-2xl font-bold">{{ stats.assigned }}</label>
        <label class="text-sm">Assigned</label>
      </div>
      <div class="stat-card text-success-600">
        <label class="text-2xl font-bold">{{ stats.approved }}</label>
        <label class="text-sm">Approved</label>
      </div>
      <div class="stat-card text-danger-600">
        <label class="text-2xl font-bold">{{ stats.rejected }}</label>
        <label class="text-sm">Rejected</label>
      </div>
      <div class="stat-card">
        <label class="text-2xl font-bold">{{ stats.total }}</label>
        <label class="text-sm">Total</label>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded shadow p-3 mb-2 flex flex-wrap items-end gap-3">
      <!-- Tracking Number -->
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-gray-600 mb-1">Tracking Number</label>
        <input
          v-model="trackingNumber"
          type="text"
          placeholder="Enter Tracking No."
          class="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          @keyup.enter="fetch"
        />
      </div>

      <!-- Name -->
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-gray-600 mb-1">Name</label>
        <input
          v-model="name"
          type="text"
          placeholder="Enter Name"
          class="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          @keyup.enter="fetch"
        />
      </div>

      <!-- Status -->
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-gray-600 mb-1">Status</label>
        <select
          v-model="statuses"
          class="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          @change="fetch"
        >
          <option :value="null">-- Filter by Status --</option>
          <option value="DRAFT">Draft</option>
          <option value="PENDING">In Queue</option>
          <option value="ASSIGNED">Assigned</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="APPROVED">Approved</option>
          <option value="QUERIED">Queried</option>
        </select>
      </div>

      <!-- Type -->
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-gray-600 mb-1">Type</label>
        <select
          v-model="typeId"
          class="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          @change="onTypeChange"
        >
          <option :value="null">-- Filter by Type --</option>
          <option
            v-for="type in primaryEntityTypes"
            :key="type.id"
            :value="type.id"
          >
            {{ type.name }}
          </option>
        </select>
      </div>

      <!-- Sub Type -->
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-gray-600 mb-1">Sub Type</label>
        <select
          v-model="subTypeId"
          class="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          @change="fetch"
        >
          <option :value="null">-- Filter by Sub Type --</option>
          <option
            v-for="type in subEntityTypes"
            :key="type.id"
            :value="type.id"
          >
            {{ type.name }}
          </option>
        </select>
      </div>

      <!-- Institution -->
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-gray-600 mb-1">Associate Register </label>
        <select
          v-model="institutionId"
          class="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          @change="fetch"
        >
          <option :value="null">-- Filter by Associate Register --</option>
          <option
            v-for="inst in institutions"
            :key="inst.id"
            :value="inst.id"
          >
            {{ inst.name }}
          </option>
        </select>
      </div>

      <!-- Date from
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-gray-600 mb-1">Date from</label>
        <input
          v-model="startRegistrationDate"
          type="date"
          class="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          @change="fetch"
        />
      </div> -->

      
    </div>

    <div class="bg-white rounded shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="min-w-full leading-normal">
          <thead>
          <tr class="border-b border-t border-grey">
            <th class="t-header">#</th>
            <th class="t-header">Tracking Number</th>
            <th class="t-header">Application Type</th>
            <th class="t-header">Name</th>
            <th class="t-header">Type</th>
            <th class="t-header">Sub Type</th>
            <th class="t-header text-center">Status</th>
            <th class="t-header text-center">Date</th>
            <th class="t-header text-center">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="(application, idx) in applications"
              :key="application.id || idx"
              :class="[(idx % 2) ? rows.even : rows.odd]"
          >
            <td class="t-body">{{ ((page - 1) * limit) + idx + 1 }}</td>
            <td class="t-body">
              <span
                class="text-blue font-bold cursor-pointer hover:underline"
                @click="open(application.id)"
              >
                {{ application.trackingNumber }}
              </span>
            </td>

            <td class="t-body">
              {{ application.applicationType ? application.applicationType.replaceAll("_", " ") : '' }}
            </td>
            <td class="t-body font-semibold">
              {{ application.name ? application.name.toUpperCase() : '' }}
            </td>
            <td class="t-body">
              {{ application.type }}
            </td>
            <td class="t-body">
              {{ application.subType || '--' }}
            </td>
            <td class="t-body text-center">
                <span
                    class="text-[10px] font-medium px-2 py-1 rounded-full inline-block"
                    :class="statusClass(application.status)"
                >
                  {{ application.status ? application.status.toUpperCase().replaceAll("_", " ") : '' }}
                </span>
            </td>
            <td class="t-body text-center text-gray-400">
              {{ dateTimeFormat(application.createdAt) }}
            </td>
            <td class="t-body text-center">
              <button
                v-if="application.status === 'PENDING' || application.status === 'ASSIGNED'"
                @click="openAssign(application)"
                class="bg-blue text-white px-2 py-1 rounded text-xs hover:bg-blue-dark mr-2"
              >
                Assign
              </button>
              <button
                @click="open(application.id)"
                class="bg-blue text-white px-2 py-1 rounded text-xs hover:bg-blue-dark mt-1"
              >
                View
              </button>
            </td>
          </tr>
          <tr v-if="applications && applications.length === 0">
            <td colspan="8" class="bg-inertgrey text-center py-4">
              <span class="text-sm">No applications submitted</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      class="mt-2 flex flex-wrap items-center justify-between"
      v-if="pagination && pagination.totalPages > 1"
    >
      <div class="flex items-center gap-2">
        <button @click="previous" :class="(page > 1) ? 'paginate-btn' : 'inert-btn'">
          Previous
        </button>
        <div class="paginate-details text-center">
          <span class="bg-blue text-white px-2 py-1 rounded-sm text-xs">
            {{ page }}/{{ pagination.totalPages }}
          </span>
        </div>
        <button @click="next" :class="(page < pagination.totalPages) ? 'paginate-btn' : 'inert-btn'">
          Next
        </button>
      </div>

      <div class="flex items-center gap-3">
        <select
          v-model.number="limit"
          class="w-full text-xs form-select border-0 paginate-details"
          @change="fetch"
        >
          <option :value="15">15</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <p class="paginate-details text-center text-xs font-bold">
          <span>{{ ((page - 1) * limit) + 1 }}</span> -
          <span v-if="limit > applications.length ">{{ pagination.totalRows }}</span>
          <span v-else>{{ page * limit }}</span>
          of {{ pagination.totalRows }}
        </p>
      </div>
    </div>
    <AppModal v-model="viewModalOpen" xl6>
      <TaskView :id="selectedTaskId" />
    </AppModal>

    <b-modal id="modal-a-assign" hide-footer ref="assignSubmissions" title="Assign to Registrar">
      <Assignment :id="selectedApplication ? (selectedApplication.taskId || selectedApplication.id) : null" type="nir" permission="can_view_non_individual_tasks"/>
    </b-modal>
  </div>
</template>

<script>
import {api} from "../../../../../app/services/api.service";
import { dateTimeFormat } from '../nir/utils/dateTimeFormat';
import AppModal from '../nir/components/AppModal.vue';
import TaskView from '../nir/views/TaskView.vue';
import Assignment from "../../../commons/Assignment.vue";

export default {
  name: 'NonIndividualApplications',
  components: { AppModal, TaskView, Assignment },
  data() {
    return {
      loading: false,
      viewModalOpen: false,
      selectedTaskId: '',
      selectedApplication: null,
      page: 1,
      limit: 15,
      applications: [],
      stats: null,
      // filters
      trackingNumber: '',
      name: '',
      statuses: null,
      typeId: null,
      subTypeId: null,
      institutionId: null,
      startRegistrationDate: null,
      endRegistrationDate: null,
      primaryEntityTypes: [],
      subEntityTypes: [],
      institutions: [],
      rows: {
        odd: 'border-t border-grey hover:bg-header_grey',
        even: 'border-t border-grey hover:bg-header_grey',
      },
      pagination: {
        currentPage: 1,
        nextPage: 0,
        totalPages: 0,
        totalRows: 0,
      },
    };
  },
  mounted() {
    this.fetch();
    this.fetchStatistics();
    this.fetchInstitutions();
    this.fetchPrimaryEntityTypes();
    try {
      localStorage.removeItem('applicationId');
    } catch (e) {
      console.error('Error removing applicationId from localStorage:', e);
    }
  },
  methods: {
    dateTimeFormat,
    fetch() {
      var self = this;
      this.loading = true;
      let path = '/nir/api/v1/applications?page=' + this.page + '&limit=' + this.limit;

      if (this.statuses) {
        path += '&status=' + encodeURIComponent(this.statuses);
      }
      if (this.trackingNumber && this.trackingNumber.trim() !== '') {
        path += '&trackingNumber=' + encodeURIComponent(this.trackingNumber.trim());
      }
      if (this.name && this.name.trim() !== '') {
        path += '&name=' + encodeURIComponent(this.name.trim());
      }
      if (this.typeId) {
        path += '&typeId=' + encodeURIComponent(this.typeId);
      }
      if (this.subTypeId) {
        path += '&subTypeId=' + encodeURIComponent(this.subTypeId);
      }
      if (this.institutionId) {
        path += '&institutionId=' + encodeURIComponent(this.institutionId);
      }
      if (this.startRegistrationDate) {
        path += '&startRegistrationDate=' + encodeURIComponent(this.startRegistrationDate);
      }
      if (this.endRegistrationDate) {
        path += '&endRegistrationDate=' + encodeURIComponent(this.endRegistrationDate);
      }

      api().get(path)
          .then(function (res) {
            if (res.data && res.data.data) {
              var data = res.data.data;
              self.applications = data.content || [];
              if (data.pagination) {
                self.pagination = data.pagination;
                if (typeof data.pagination.currentPage === 'number') {
                  self.page = data.pagination.currentPage;
                }
              } else {
                self.pagination = { currentPage: 1, nextPage: 0, totalPages: 0, totalRows: 0 };
              }
            } else {
              self.applications = [];
              self.pagination = { currentPage: 1, nextPage: 0, totalPages: 0, totalRows: 0 };
            }
            self.loading = false;
          })
          .catch(function () {
            self.loading = false;
          });
    },
    fetchStatistics() {
      const self = this;
      api().get('/nir/api/v1/applications/stats')
          .then(function (res) {
            if (res.data && res.data.data) {
              self.stats = res.data.data;
            }
          });
    },
    fetchInstitutions() {
      var self = this;
      api().get('/nir/api/v1/institutions?page=1&limit=1000')
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.content) {
              self.institutions = res.data.data.content;
            } else {
              self.institutions = [];
            }
          })
          .catch(function () {
            self.institutions = [];
          });
    },
    fetchPrimaryEntityTypes() {
      var self = this;
      api().get('/nir/api/v1/configurations/entity-types/primary')
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.data) {
              self.primaryEntityTypes = res.data.data.data;
            } else {
              self.primaryEntityTypes = [];
            }
          })
          .catch(function () {
            self.primaryEntityTypes = [];
          });
    },
    fetchSubEntityTypes(parentId) {
      var self = this;
      if (!parentId) {
        this.subEntityTypes = [];
        return;
      }
      api().get('/nir/api/v1/configurations/entity-types?parentId=' + encodeURIComponent(parentId))
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.data) {
              self.subEntityTypes = res.data.data.data;
            } else {
              self.subEntityTypes = [];
            }
          })
          .catch(function () {
            self.subEntityTypes = [];
          });
    },
    onTypeChange() {
      this.subTypeId = null;
      this.fetchSubEntityTypes(this.typeId);
      this.fetch();
    },
    next() {
      if (this.pagination && this.page < this.pagination.totalPages) {
        this.page += 1;
        this.fetch();
      }
    },
    previous() {
      if (this.page > 1) {
        this.page -= 1;
        this.fetch();
      }
    },
    open(id) {
      this.viewModalOpen = true;
      this.selectedTaskId = id;
    },
    openAssign(application) {
      this.selectedApplication = application;
      this.$refs.assignSubmissions.show();
    },
    resume(id) {
      try {
        localStorage.setItem('applicationId', id);
      } catch (e) {
        console.error('Failed to set applicationId in localStorage:', e);
      }
      if (this.$router) {
        this.$router.push({ name: 'nir-registration-new' });
      }
    },
    statusClass(status) {
      var map = {
        DRAFT: 'bg-gray-100 text-gray-400',
        QUERIED: 'bg-red-100 text-gray-400',
        PENDING: 'bg-gray-100 text-gray-600',
        PREAPPROVAL: 'bg-success-50 text-success-600 border border-success-200',
        SUBMITTED: 'bg-gray-100 text-gray-800',
        ASSIGNED: 'bg-blue-100 text-blue-800',
        APPROVED: 'bg-success-100 text-success-800',
        AUTO_ADMITTED: 'bg-success-100 text-success-800',
        REJECTED: 'bg-danger-100 text-danger-800',
      };
      return map[status] || 'bg-gray-100 text-gray-600';
    },
  },
};
</script>

<style scoped>
@import "../../../../../assets/styles/table.css";

.flex-1 { flex: 1; }
.flex { display: flex; }
.flex-col { flex-direction: column; }
.p-2 { padding: 0.5rem; }
.grid { display: grid; }
.grid-cols-6 { grid-template-columns: repeat(6, minmax(0, 1fr)); }
.gap-2 { gap: 0.5rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.stat-card {
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  align-items: center;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  padding: 0.75rem;
}

.text-success-600 { color: #059669; }
.text-danger-600 { color: #dc2626; }
.flex-gap-2 { display: flex; gap: 0.5rem; }

.paginate-details {
  @apply px-3 py-2 bg-white my-2 text-sm text-gray;
}

.paginate-btn {
  @apply px-3 py-2 bg-white border border-white my-2 text-sm text-blue w-full;
}

.paginate-btn:hover {
  @apply border border-blue text-blue rounded-sm;
}

.inert-btn {
  @apply px-3 py-2 bg-white my-2 text-sm text-grey w-full;
}
</style>
